import React, { Component, Fragment } from "react";
import { Redirect, NavLink } from "react-router-dom";
import moment from "moment";
import {
  Grid,
  Col,
  Row,
  Tabs,
  Tab,
  Button,
  Label,
  OverlayTrigger,
  Tooltip,
  Panel,
  Badge
} from "react-bootstrap";
import debounce from "lodash/debounce";
import snakeCase from "lodash/snakeCase";
import remove from "lodash/remove";
import unionBy from "lodash/unionBy";
import { Link } from "react-router-dom";

import { get, put, download } from "../../../utils/DeApi";
import {
  publish,
  subscribe,
  unsubscribe,
  presence
} from "../../../utils/Broadcast";

import "./Theme.css";
import NoteList from "../NoteList/NoteList";
import ThemeEdit from "../ThemeEdit/ThemeEdit";
import ThemeDelete from "../ThemeDelete/ThemeDelete";
import ThemeCreate from "../ThemeCreate/ThemeCreate";
import RichTextEditor from "../../RichTextEditor/RichTextEditor";
import Loader from "../../Loader/Loader";
import ErrorHandler from "../../ErrorHandler/ErrorHandler";
import ReAuth from "../../App/ReAuth/ReAuth";
import PageVisibility from "../../App/PageVisibility/PageVisibility";
import { FocusModeContext } from "../FocusModeContext/FocusModeContext";

/**
 * Handles rendering of writing view for a theme
 */
class Theme extends Component {
  constructor(props) {
    super(props);
    this.subscribedPromises = [];
    this.state = {
      reAuthenticate: false,
      syncing: false,
      isMobile: /mobi/i.test(navigator.userAgent.toLowerCase()),
      connections: [],

      matchCase: true,
      wholeWord: true,
      activePillarSelections: [],
      wordMatch: [],
      themeMatchCount: 0
    };

    this.handleThemeCreated = this._handleThemeCreated.bind(this);
    this.handleThemeDeleted = this._handleThemeDeleted.bind(this);
    this.handleReauthenticated = this._handleReauthenticated.bind(this);
    this.handleThemeUpdated = this._handleThemeUpdated.bind(this);
    this.handleThemeExport = this._handleThemeExport.bind(this);
    this.handleRichTextUpdated = this._handleRichTextUpdated.bind(this);
    this.handleThemeEditing = this._handleThemeEditing.bind(this);
    this.findReplaceStatus = this._findReplaceStatus.bind(this);

    this.handleVisibilityChange = debounce(
      visibilty => {
        this._handleVisibilityChange(visibilty);
      },
      60000,
      {
        leading: true,
        trailing: false
      }
    );

    this.handleThemeBodyUpdated = debounce(
      (body, success, error) => {
        this.updateThemeBody(body, success, error);
      },
      1000,
      {
        maxWait: 10000
      }
    );

    this.handleThemeStatsUpdated = debounce((stats, success, error) => {
      this.updateThemeStats(stats, success, error);
    }, 1000);

    this.handleSearchWordMatch = debounce((params, success, error) => {
      this.searchWordMatch(params, success, error);
    }, 300);
  }

  _handleThemeDeleted() {
    this.setState({ deleted: true });
  }

  _handleThemeCreated(theme) {
    this.setState(function(prevState, props) {
      prevState.themes.push(theme);
      return {
        themes: prevState.themes
      };
    });
  }

  _handleReauthenticated() {
    this.setState({ reAuthenticate: false });
  }

  _handleThemeUpdated(theme) {
    this.setState(function(prevState, props) {
      return {
        themes: prevState.themes.map(item => {
          if (item.id === theme.id) return theme;
          else return item;
        }),
        theme: theme
      };
    });
  }

  _handleRichTextUpdated(body, successCallback, errorCallback) {
    const { theme, connections } = this.state;
    const channel = `Theme.${theme.id}`;

    if (connections.length > 1) {
      publish(channel, "typing", { status: true });
      this.handleThemeBodyUpdated(
        body,
        response => {
          publish(channel, "typing", { status: false });
          successCallback(response);
        },
        error => {
          publish(channel, "typing", { status: false });
          errorCallback(error);
        }
      );
    } else {
      this.handleThemeBodyUpdated(body, successCallback, errorCallback);
    }
  }

  _handleThemeExport() {
    const { project } = this.props;
    const { theme } = this.state;

    let exportPromise = download(`/manuscript/${project.id}/themes`, {
      params: {
        themes: [theme.id]
      }
    });

    const fileName = snakeCase(theme.title) + ".docx";

    exportPromise.promise
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;

        link.setAttribute("download", fileName);
        document.body.appendChild(link);

        link.click();
        // success
      })
      .catch(error => {
        // error
      });
    this.subscribedPromises.push(exportPromise);
  }

  _handleThemeEditing(editing) {
    this.setState({ editing: editing });
  }

  _handleVisibilityChange(visibilty) {
    const { editing, theme } = this.state;
    if (
      moment().diff(moment.utc(theme.updatedAt), "minutes") > 30 &&
      visibilty &&
      !editing
    ) {
      this.setState({ theme: "", themes: [] });
      this.fetchTheme();
      this.fetchThemes();
      this.fetchThemeTypes();
    }
  }

  _findReplaceStatus(status) {
    this.setState({
      activePillarSelections: status.activePillarSelections,
      themeMatchCount: status.activePillarSelections.length > 0 ? 1 : 0,
      isOpen: status.isOpen,
      wordMatch: []
    });

    if (status.isOpen && status.find && status.find.length > 0) {
      this.handleSearchWordMatch({
        query: status.find,
        matchCase: status.matchCase,
        wholeWord: status.wholeWord,
        pillar: "theme"
      });
    }
  }

  componentDidMount() {
    this.fetchTheme();
    this.fetchThemes();
    this.fetchThemeTypes();
    this.registerBroadcasting();
  }

  componentDidUpdate(prevProps) {
    if (this.props.themeId !== prevProps.themeId) {
      this.setState({
        theme: "",
        themes: [],
        error: ""
      });
      this.fetchTheme();
      this.fetchThemes();
      this.fetchThemeTypes();
    }
  }

  componentWillUnmount() {
    this.subscribedPromises.forEach(function(promise) {
      promise.cancel();
    });
    this.handleThemeBodyUpdated.cancel();
    unsubscribe(`Theme.${this.props.themeId}`);
  }

  registerBroadcasting() {
    const channel = `Theme.${this.props.themeId}`;
    subscribe(channel, "typing", event => {
      if (event.status) {
        this.setState({ syncing: true });
      } else {
        this.setState({ theme: "", syncing: false });
        this.fetchTheme();
      }
    });
    presence(channel)
      .here(connections => {
        this.setState({ connections: connections || [] });
      })
      .joining(connection => {
        this.setState((state, props) => {
          return {
            connections: unionBy(state.connections, [connection], "name") || []
          };
        });
      })
      .leaving(connection => {
        this.setState((state, props) => {
          return {
            connections: remove(state.connections, item => {
              return connection.name !== item.name;
            })
          };
        });
      });
  }

  fetchTheme() {
    let themePromise = get("/themes/" + this.props.themeId);
    themePromise.promise
      .then(response => {
        this.setState({
          theme: response.data,
          error: ""
        });
      })
      .catch(error => {
        !error.isCanceled &&
          this.setState({
            error: error
          });
      });
    this.subscribedPromises.push(themePromise);
  }

  fetchThemes() {
    let themesPromise = get("/themes?project=" + this.props.project.id);
    themesPromise.promise
      .then(response => {
        this.setState({
          themes: response.data,
          error: ""
        });
      })
      .catch(error => {
        !error.isCanceled &&
          this.setState({
            error: error
          });
      });
    this.subscribedPromises.push(themesPromise);
  }

  fetchThemeTypes() {
    let themeTypesPromise = get("/theme-types");
    themeTypesPromise.promise
      .then(response => {
        this.setState({
          themeTypes: response.data
        });
      })
      .catch(error => {
        !error.isCanceled &&
          this.setState({
            error: error
          });
      });
    this.subscribedPromises.push(themeTypesPromise);
  }

  searchWordMatch = params => {
    this.setState({ wordMatchLoading: true });
    let themesPromise = get(
      `/projects/${this.props.project.id}/pillar-search`,
      {
        params: params
      }
    );
    themesPromise.promise
      .then(response => {
        const wordMatch = response.data;
        let count = this.state.themeMatchCount;
        wordMatch.forEach(item => {
          if (item.match > 0 && item.themeId !== this.props.themeId) count++;
        });

        this.setState({
          wordMatch: wordMatch,
          themeMatchCount: count,
          wordMatchLoading: false,
          matchError: ""
        });
      })
      .catch(error => {
        !error.isCanceled &&
          this.setState({
            wordMatchLoading: false,
            matchError: error
          });
      });

    this.subscribedPromises.push(themesPromise);
  };

  updateThemeBody(body, successCallback, errorCallback) {
    const { theme } = this.state;
    let updateBodyPromise = put("/themes/" + theme.id + "/body", {
      body: JSON.stringify(body)
    });

    this.setState({ reAuthenticate: false });

    updateBodyPromise.promise
      .then(response => {
        this.setState({ reAuthenticate: false, theme: response.data }, () => {
          successCallback(response);
        });
      })
      .catch(error => {
        if (!error.isCanceled) {
          if (error.status === 401) this.setState({ reAuthenticate: true });
          errorCallback(error);
        }
      });
    this.subscribedPromises.push(updateBodyPromise);
  }

  updateThemeStats(stats, successCallback, errorCallback) {
    if (stats) {
      let updateStatsPromise = put(
        "/themes/" + this.state.theme.id + "/stats",
        stats
      );
      this.setState({ reAuthenticate: false });
      updateStatsPromise.promise
        .then(response => {
          successCallback(response);
        })
        .catch(error => {
          if (!error.isCanceled) {
            if (error.status === 401) this.setState({ reAuthenticate: true });
            errorCallback(error);
          }
        });
      this.subscribedPromises.push(updateStatsPromise);
    }
  }

  render() {
    const {
      deleted,
      error,
      theme,
      themes,
      themeTypes,
      syncing,
      reAuthenticate,
      isMobile,
      isOpen,
      themeMatchCount,
      wordMatchLoading
    } = this.state;
    const { project } = this.props;

    if (deleted) return <Redirect to={"/projects/" + project.id + "/themes"} />;

    if (error) return <ErrorHandler error={error} />;
    if (!theme || !themes) return <Loader />;

    let previous = themes[parseInt(theme.orderId) - 2];
    let next = themes[parseInt(theme.orderId)];

    return (
      <FocusModeContext.Consumer>
        {({ isFocused }) => (
          <Grid fluid className="PillarView">
            <Row className="PillarCanvas">
              <Panel
                id="collapsible-panel-example-2"
                expanded={this.state.toggle}
                onToggle={() => {}}
                className="mb-0 hidden-md hidden-lg"
              >
                <Panel.Heading>
                  <div className="panel-title">
                    <Row>
                      <Col xs={2}>
                        {previous ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip">
                                Go to previous theme
                              </Tooltip>
                            }
                          >
                            <Link
                              className="pull-left text-left"
                              style={{ width: 40 }}
                              to={`/projects/${project.id}/themes/${previous.id}`}
                            >
                              <i className="material-icons md-24">
                                arrow_backward
                              </i>
                            </Link>
                          </OverlayTrigger>
                        ) : (
                          <span />
                        )}
                      </Col>
                      <Col
                        xs={8}
                        onClick={() =>
                          this.setState({
                            toggle: !this.state.toggle
                          })
                        }
                      >
                        <h4 className="pt-0 pb-0 mb-0 text-center">
                          {theme.title}
                        </h4>
                      </Col>
                      <Col xs={2}>
                        {next ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip">Go to next theme</Tooltip>
                            }
                          >
                            <Link
                              className="pull-right text-right"
                              style={{ width: 40 }}
                              to={`/projects/${project.id}/themes/${next.id}`}
                            >
                              <i className="material-icons md-24">
                                arrow_forward
                              </i>
                            </Link>
                          </OverlayTrigger>
                        ) : (
                          <span />
                        )}
                      </Col>
                    </Row>
                  </div>
                </Panel.Heading>
                <Panel.Collapse>
                  <Panel.Body>
                    <p className="text-right">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-edit">
                            Edit title and summary
                          </Tooltip>
                        }
                      >
                        <span>
                          <ThemeEdit
                            theme={theme}
                            themeTypes={themeTypes}
                            onThemeUpdated={this.handleThemeUpdated}
                            onThemeEditing={this.handleThemeEditing}
                          />
                        </span>
                      </OverlayTrigger>
                    </p>
                    {theme.description ? (
                      <p className="PillarDescription mt-xs mb-sm">
                        {theme.description}
                      </p>
                    ) : (
                      ""
                    )}
                    <ul className="list-inline">
                      <li>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="tooltip-export">
                              Export this theme
                            </Tooltip>
                          }
                        >
                          <Button
                            className="btn-secondary"
                            bsSize="xs"
                            onClick={this.handleThemeExport}
                          >
                            Export
                          </Button>
                        </OverlayTrigger>
                      </li>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-delete">
                            Delete this theme
                          </Tooltip>
                        }
                      >
                        <li>
                          <ThemeDelete
                            theme={theme}
                            onThemeDeleted={this.handleThemeDeleted}
                          />
                        </li>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-new">Create a new theme</Tooltip>
                        }
                      >
                        <li>
                          <ThemeCreate
                            project={project}
                            onThemeCreated={this.handleThemeCreated}
                            label="New"
                            className="btn-secondary btn-xs"
                          />
                        </li>
                      </OverlayTrigger>
                    </ul>
                  </Panel.Body>
                </Panel.Collapse>
              </Panel>
              <Col xs={12} md={3} className="PillarLeft hidden-xs hidden-sm">
                <div className={`mt-md mb-md ${isFocused ? "hidden" : ""}`}>
                  <h4 className="pt-xs pb-xs">{theme.title}</h4>
                  <Tabs defaultActiveKey={1} id="themes-meta-data-tabs">
                    <Tab eventKey={1} title="Summary">
                      <p className="text-right">
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="tooltip-edit">
                              Edit title, type, and summary
                            </Tooltip>
                          }
                        >
                          <span>
                            <ThemeEdit
                              theme={theme}
                              themeTypes={themeTypes}
                              onThemeUpdated={this.handleThemeUpdated}
                              onThemeEditing={this.handleThemeEditing}
                              bsSize="xs"
                              bsStyle="default"
                            />
                          </span>
                        </OverlayTrigger>
                      </p>
                      {theme.description ? (
                        <p className="PillarDescription mt-xs mb-sm">
                          {theme.description}
                        </p>
                      ) : (
                        <p className="sub-text  mt-xs mb-sm">...</p>
                      )}
                      <Label bsStyle="default" className="tag text-capitalize">
                        {theme.themeType.name}
                      </Label>
                    </Tab>
                    <Tab
                      eventKey={2}
                      title={
                        <span>
                          All Themes{" "}
                          {isOpen && (
                            <Fragment>
                              {wordMatchLoading ? (
                                <div class="find-loader"></div>
                              ) : (
                                <Fragment>
                                  {themeMatchCount > 0 && (
                                    <Badge>{themeMatchCount}</Badge>
                                  )}
                                </Fragment>
                              )}
                            </Fragment>
                          )}
                        </span>
                      }
                    >
                      {this.renderThemes()}
                    </Tab>
                  </Tabs>

                  <div className="mt-sm mb-sm">
                    <ul className="list-inline">
                      <li>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id="tooltip-export">
                              Export this theme
                            </Tooltip>
                          }
                        >
                          <Button
                            className="btn-secondary"
                            bsSize="xs"
                            onClick={this.handleThemeExport}
                          >
                            Export
                          </Button>
                        </OverlayTrigger>
                      </li>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-delete">
                            Delete this theme
                          </Tooltip>
                        }
                      >
                        <li>
                          <ThemeDelete
                            theme={theme}
                            onThemeDeleted={this.handleThemeDeleted}
                          />
                        </li>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="tooltip-new">Create a new theme</Tooltip>
                        }
                      >
                        <li>
                          <ThemeCreate
                            project={project}
                            onThemeCreated={this.handleThemeCreated}
                            label="New"
                            className="btn-secondary btn-xs"
                          />
                        </li>
                      </OverlayTrigger>
                    </ul>
                    <ul className="list-inline">
                      <li>
                        {previous ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip-previous">
                                Go to previous theme
                              </Tooltip>
                            }
                          >
                            <Button
                              className="btn-secondary"
                              bsSize="xs"
                              componentClass={Link}
                              to={`/projects/${project.id}/themes/${previous.id}`}
                              href={`/projects/${project.id}/themes/${previous.id}`}
                            >
                              Previous
                            </Button>
                          </OverlayTrigger>
                        ) : (
                          <Button
                            disabled
                            bsSize="xs"
                            className="btn-secondary"
                          >
                            Previous
                          </Button>
                        )}
                      </li>

                      <li>
                        {next ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip-next">
                                Go to next theme
                              </Tooltip>
                            }
                          >
                            <Button
                              className="btn-secondary"
                              bsSize="xs"
                              componentClass={Link}
                              to={`/projects/${project.id}/themes/${next.id}`}
                              href={`/projects/${project.id}/themes/${next.id}`}
                            >
                              Next
                            </Button>
                          </OverlayTrigger>
                        ) : (
                          <Button
                            disabled
                            bsSize="xs"
                            className="btn-secondary"
                          >
                            Next
                          </Button>
                        )}
                      </li>
                    </ul>
                  </div>
                </div>
              </Col>
              <Col xs={12} md={6} className="PillarCenter">
                <div className={!isMobile ? "mt-md mb-xl" : ""}>
                  <Row className="PillarEditor">
                    <Col xs={12}>
                      <RichTextEditor
                        richText={theme.body}
                        controls={!isMobile}
                        counter={true}
                        focusMode={true}
                        findReplaceStatus={this.findReplaceStatus}
                        onRichTextUpdated={this.handleRichTextUpdated}
                        onStatsUpdated={this.handleThemeStatsUpdated}
                        placeholder="Start writing..."
                        syncing={syncing}
                      />
                    </Col>
                  </Row>
                </div>
              </Col>
              <Col xs={12} md={3} className="PillarRight">
                <div className={`mt-md mb-xl ${isFocused ? "hidden" : ""}`}>
                  <NoteList pillarId={theme.id} pillarType="theme" />
                </div>
              </Col>
              {!!reAuthenticate && (
                <ReAuth onReauthenticated={this.handleReauthenticated} />
              )}
              <PageVisibility
                onVisibilityChange={this.handleVisibilityChange}
              />
            </Row>
          </Grid>
        )}
      </FocusModeContext.Consumer>
    );
  }

  renderThemes() {
    const { project } = this.props;
    const { activePillarSelections, isOpen, wordMatch } = this.state;
    return (
      <div className="list-group numbered-list-group">
        {this.state.themes.map((theme, index) => {
          return (
            <NavLink
              key={theme.id}
              to={"/projects/" + project.id + "/themes/" + theme.id}
              activeClassName="active"
              className="list-group-item"
            >
              {(isOpen && wordMatch.length > 0) ||
              (activePillarSelections &&
                isOpen &&
                activePillarSelections.length > 0) ? (
                <Fragment>
                  {theme.title && theme.title.length > 28
                    ? theme.title.slice(0, 25) + "..."
                    : theme.title}
                </Fragment>
              ) : (
                theme.title
              )}

              {this.props.themeId === theme.id ? (
                <Fragment>
                  {activePillarSelections &&
                    isOpen &&
                    activePillarSelections.length > 0 && (
                      <Badge className="pull-right">
                        {activePillarSelections.length}
                      </Badge>
                    )}
                </Fragment>
              ) : (
                <Fragment>
                  {wordMatch &&
                    isOpen &&
                    wordMatch.length > 0 &&
                    wordMatch.map((item, index) => {
                      if (item.themeId === theme.id && item.match > 0)
                        return (
                          <Badge key={index} className="pull-right">
                            {item.match}
                          </Badge>
                        );
                      else return <Fragment key={index} />;
                    })}
                </Fragment>
              )}
            </NavLink>
          );
        })}
      </div>
    );
  }
}

export default Theme;
